import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import { organizationApi } from '../../../../services/api';
import { useAlert } from '../../../../contexts/AlertContext';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmployeeSearch from '../../../common/EmployeeSearch';
import ClusterSelector from '../../../common/ClusterSelector';
import OrgStructureTypeSelector from '../../../common/OrgStructureTypeSelector';
import BusinessUnitsTab from './BusinessUnitsTab';
//import DepartmentsTab from './DepartmentsTab';
import CompaniesTab from './CompaniesTab';
import { REGION_STRUCTURE_TYPE_CODE } from '../../../../constants/orgStructureTypes';
import { REGION_SYSTEM_OF_RECORD_OPTIONS } from '../../../../constants/discrepancyRadarOnly';
import {
  limitChange,
  isLimitLocked,
  lockedNote,
  isLowering,
  impactSentence,
} from '../../../../utils/clusterOpenContractLimit';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  BackButton,
  HeaderButton,
  FormContainer,
  TabContainer,
  TabButton,
  FormSection,
  SectionTitle,
  FormRow,
} from '../../../ProjectsManagement/ProjectForm/styles';

const TABS = {
  CLUSTER_DATA: 'CLUSTER_DATA',
  COMPANIES: 'COMPANIES',
  BUSINESS_UNITS: 'BUSINESS_UNITS',
  //DEPARTMENTS: 'DEPARTMENTS'
};

const ClusterForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(id ? true : false);
  const [activeTab, setActiveTab] = useState(TABS.CLUSTER_DATA);
  const [formData, setFormData] = useState({
    name: '',
    managerId: '',
    parentClusterId: null,
    orgStructureTypeId: null, // ADR-119: optional classification
    systemOfRecord: null // ADR-130 D1: Regions only
  });
  const [selectedManager, setSelectedManager] = useState(null);
  // ADR-130 D1: the stored value (to detect a change) and the reason for it
  const [initialSystemOfRecord, setInitialSystemOfRecord] = useState(null);
  const [systemOfRecordChangeReason, setSystemOfRecordChangeReason] = useState('');
  const [orgStructureTypes, setOrgStructureTypes] = useState([]);
  // ADR-167: Region maximum open contracts per person ('' = the default)
  const [maxOpenContractsInput, setMaxOpenContractsInput] = useState('');
  const [initialMaxOpenContracts, setInitialMaxOpenContracts] = useState(null);
  const [maxOpenContractsChangeReason, setMaxOpenContractsChangeReason] = useState('');
  const [openContractLimitDefault, setOpenContractLimitDefault] = useState(1);
  const [hasChildClusters, setHasChildClusters] = useState(false);
  // ADR-167 D6: people a lowering would leave above the new limit
  const [limitImpact, setLimitImpact] = useState({ loading: false, people: [], newLimit: null });
  const [confirmLowerAnyway, setConfirmLowerAnyway] = useState(false);

  const mode = id ? 'edit' : 'add';

  useEffect(() => {
    if (mode === 'edit') {
      fetchCluster();
    }
  }, [mode, id]);

  // ADR-130 D1: the type list tells us whether the chosen type is a Region
  useEffect(() => {
    let cancelled = false;
    organizationApi.getOrgStructureTypes()
      .then((types) => { if (!cancelled) setOrgStructureTypes(Array.isArray(types) ? types : []); })
      .catch(() => { if (!cancelled) setOrgStructureTypes([]); });
    return () => { cancelled = true; };
  }, []);

  const isRegion = useMemo(() => {
    const type = orgStructureTypes.find((t) => t.id === formData.orgStructureTypeId);
    return type?.code === REGION_STRUCTURE_TYPE_CODE;
  }, [orgStructureTypes, formData.orgStructureTypeId]);

  // The value that will be sent: a non-Region can hold no system of record.
  const effectiveSystemOfRecord = isRegion ? (formData.systemOfRecord || null) : null;
  const systemOfRecordChanged = mode === 'edit' && effectiveSystemOfRecord !== initialSystemOfRecord;

  // ADR-167 D1/D1a/D6 — the limit field
  const limitLocked = isLimitLocked({ isRegion, systemOfRecord: effectiveSystemOfRecord });
  const limit = limitChange({ mode, isRegion, input: maxOpenContractsInput, initialValue: initialMaxOpenContracts });
  const limitLowering = mode === 'edit' && limit.changed && isLowering({
    isRegion,
    initialSystemOfRecord,
    systemOfRecord: effectiveSystemOfRecord,
    initialValue: initialMaxOpenContracts,
    newValue: limit.value,
    defaultLimit: openContractLimitDefault,
  });
  const peopleAboveNewLimit = limitLowering ? limitImpact.people : [];
  // D3a: a cluster with sub-clusters cannot become a Region
  const regionWithChildren = isRegion && hasChildClusters;

  // D6: preview who a lowering would leave above the new limit (debounced)
  useEffect(() => {
    if (!limitLowering || !id) {
      setLimitImpact({ loading: false, people: [], newLimit: null });
      return undefined;
    }
    let cancelled = false;
    setLimitImpact((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(() => {
      organizationApi.getClusterOpenContractLimitImpact(id, limit.value ?? '')
        .then((impact) => {
          if (!cancelled) setLimitImpact({ loading: false, people: impact?.people || [], newLimit: impact?.newLimit ?? null });
        })
        .catch(() => { if (!cancelled) setLimitImpact({ loading: false, people: [], newLimit: null }); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [limitLowering, id, limit.value]);

  const fetchCluster = async () => {
    try {
      setIsLoading(true);
      const clusterData = await organizationApi.getClusterById(id);
      setFormData({
        name: clusterData.name,
        managerId: clusterData.manager?.id || '',
        parentClusterId: clusterData.parentCluster?.id ?? null,
        orgStructureTypeId: clusterData.orgStructureType?.id ?? null,
        systemOfRecord: clusterData.systemOfRecord ?? null
      });
      setInitialSystemOfRecord(clusterData.systemOfRecord ?? null);
      setSystemOfRecordChangeReason('');
      // ADR-167
      setInitialMaxOpenContracts(clusterData.maxOpenContractsPerEmployee ?? null);
      setMaxOpenContractsInput(clusterData.maxOpenContractsPerEmployee == null ? '' : String(clusterData.maxOpenContractsPerEmployee));
      setMaxOpenContractsChangeReason('');
      setOpenContractLimitDefault(clusterData.openContractLimitDefault ?? 1);
      setHasChildClusters(Array.isArray(clusterData.childClusters) && clusterData.childClusters.length > 0);
      setSelectedManager(clusterData.manager || null);
    } catch (err) {
      showAlert(err.message || 'Failed to fetch cluster', 'error', err);
      navigate('/organization/clusters');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    setFormData(prev => ({
      ...prev,
      [field]: value ?? ''
    }));
  };

  // Parent picker: ClusterSelector hands back '' for its "No parent" option
  // and an id otherwise; the API expects null for "no parent".
  const handleParentChange = (value) => {
    setFormData(prev => ({ ...prev, parentClusterId: value || null }));
  };

  // ADR-119: null clears the type server-side (undefined would leave it untouched)
  const handleStructureTypeChange = (value) => {
    setFormData(prev => ({ ...prev, orgStructureTypeId: value || null }));
  };

  const handleSystemOfRecordChange = (event) => {
    const value = event.target.value || null;
    setFormData(prev => ({ ...prev, systemOfRecord: value }));
  };

  const handleManagerChange = (newManager) => {
    setSelectedManager(newManager);
    setFormData(prev => ({
      ...prev,
      managerId: newManager?.id || ''
    }));
  };

  const handleSubmit = async ({ keepExistingContracts = false } = {}) => {
    if (!formData.name) {
      showAlert('Please fill in the cluster name', 'error');
      return;
    }

    // ADR-167 D3a
    if (regionWithChildren) {
      showAlert('This cluster has sub-clusters; a Region cannot contain other clusters.', 'error');
      return;
    }

    // ADR-167 D1: the limit must be a whole number of 1 or more, or empty for the default
    if (limit.invalid) {
      showAlert('Maximum open contracts per person must be a whole number of 1 or more, or empty for the default', 'error');
      return;
    }

    // ADR-167 D7: a change is audited
    if (mode === 'edit' && limit.changed && !maxOpenContractsChangeReason.trim()) {
      showAlert('Please give a reason for changing the maximum open contracts', 'error');
      return;
    }

    // ADR-167 D6: lowering below what people hold needs "Lower anyway"
    if (peopleAboveNewLimit.length > 0 && !keepExistingContracts) {
      showAlert(impactSentence({ people: peopleAboveNewLimit, newLimit: limitImpact.newLimit, regionName: formData.name }), 'error');
      return;
    }

    // ADR-130 D1: a change to an existing Region's system of record is audited
    if (systemOfRecordChanged && !systemOfRecordChangeReason.trim()) {
      showAlert('Please give a reason for changing the system of record', 'error');
      return;
    }

    const payload = {
      ...formData,
      systemOfRecord: effectiveSystemOfRecord,
      ...(systemOfRecordChanged || (mode === 'add' && effectiveSystemOfRecord)
        ? { systemOfRecordChangeReason: systemOfRecordChangeReason.trim() || undefined }
        : {}),
      // ADR-167: sent only when it changes (absent = untouched)
      ...(limit.changed
        ? {
          maxOpenContractsPerEmployee: limit.value,
          maxOpenContractsChangeReason: maxOpenContractsChangeReason.trim() || undefined,
        }
        : {}),
      ...(keepExistingContracts ? { keepExistingContracts: true } : {})
    };

    try {
      if (mode === 'add') {
        await organizationApi.createCluster(payload);
        showAlert('Cluster created successfully', 'success');
      } else {
        await organizationApi.updateCluster(id, payload);
        showAlert('Cluster updated successfully', 'success');
      }
      navigate('/organization/clusters');
    } catch (err) {
      // ADR-167 D6: someone opened a contract since the preview — show the fresh list
      if (err.status === 409 && Array.isArray(err.data?.people)) {
        setLimitImpact({ loading: false, people: err.data.people, newLimit: err.data.newLimit ?? null });
      }
      showAlert(err.message || `Failed to ${mode} cluster`, 'error', err);
    }
  };



  const renderClusterData = () => (
    <FormContainer>
      <FormSection>
        <SectionTitle>Basic Information</SectionTitle>
        <FormRow>
          <TextField
            fullWidth
            label="Cluster Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            required
          />
          {/* ADR-119: shared selector replaces the hand-rolled parent picker;
              the current cluster and its descendants are disabled to prevent cycles. */}
          <ClusterSelector
            label="Parent Cluster"
            placeholder="No parent cluster"
            value={formData.parentClusterId || ''}
            onChange={handleParentChange}
            allowSelectAll
            allOptionLabel="No parent cluster"
            allOptionValue=""
            excludeSubtreeOf={id || null}
            showStructureType
            // ADR-167 D3a: a Region cannot contain other clusters
            disabledStructureTypeCodes={[REGION_STRUCTURE_TYPE_CODE]}
            disabledStructureTypeSuffix=" (a Region cannot contain clusters)"
          />
        </FormRow>
        <FormRow>
          <OrgStructureTypeSelector
            label="Organizational Structure Type"
            value={formData.orgStructureTypeId}
            onChange={handleStructureTypeChange}
            showEmptyOption
            emptyOptionLabel="None"
            helperText="Classifies this Cluster (e.g. Global, Vertical, Capability, Region). Managed under Organization › Organizational Structure Types."
          />
        </FormRow>
        {regionWithChildren && (
          <FormRow>
            <Alert severity="error" sx={{ width: '100%' }}>
              This cluster has sub-clusters; a Region cannot contain other clusters. Move them first, or choose another type.
            </Alert>
          </FormRow>
        )}
        {/* ADR-130 D1: which system is authoritative for people hired into this Region */}
        {isRegion && (
          <FormRow>
            <FormControl fullWidth>
              <InputLabel id="cluster-system-of-record-label">System of record</InputLabel>
              <Select
                labelId="cluster-system-of-record-label"
                label="System of record"
                value={formData.systemOfRecord || ''}
                onChange={handleSystemOfRecordChange}
              >
                <MenuItem value=""><em>Not decided</em></MenuItem>
                {REGION_SYSTEM_OF_RECORD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Which system is the source of truth for people hired into this Region. An HR sync here
                means its daily Direct Representative Discrepancy run checks that everyone active in the
                Region still arrives through that sync. Radar means radar itself is the source of truth
                and the Region is not checked.
              </FormHelperText>
            </FormControl>
          </FormRow>
        )}
        {/* ADR-167 D1/D1a/D8: how many contracts one person in this Region may hold */}
        {isRegion && (
          <FormRow>
            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 1, step: 1 }}
                label="Maximum open contracts per person"
                value={limitLocked ? (maxOpenContractsInput || '1') : maxOpenContractsInput}
                onChange={(e) => setMaxOpenContractsInput(e.target.value)}
                disabled={limitLocked && !(initialMaxOpenContracts > 1)}
                placeholder={`Empty — uses the default (${openContractLimitDefault})`}
                InputLabelProps={{ shrink: true }}
                error={limit.invalid}
                helperText={limitLocked
                  ? lockedNote(effectiveSystemOfRecord)
                  : `How many contracts one person hired into this Region may hold at the same time. 1 means one contract per person (concurrent contracts off). Empty uses the default (${openContractLimitDefault}). Changing it never closes a contract.`}
              />
              {limitLowering && limitImpact.loading && (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>Checking who holds more contracts…</Typography>
              )}
              {peopleAboveNewLimit.length > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  action={(
                    <Button color="inherit" size="small" variant="outlined" onClick={() => setConfirmLowerAnyway(true)}>
                      Lower anyway
                    </Button>
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {impactSentence({ people: peopleAboveNewLimit, newLimit: limitImpact.newLimit, regionName: formData.name })}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    End or cancel their extra contracts first, or lower anyway and let them keep their contracts.
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {peopleAboveNewLimit.map((p) => (
                      <li key={p.employeeId}>
                        <MuiLink href={`/employees/${p.employeeId}/edit?tab=EMPLOYMENT_EPISODES`} target="_blank" rel="noopener noreferrer">
                          {p.fullName || p.employeeId}
                        </MuiLink>
                        {` — ${p.openContracts} open contracts`}
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          </FormRow>
        )}
        {mode === 'edit' && limit.changed && (
          <FormRow>
            <TextField
              fullWidth
              required
              label="Reason for the maximum open contracts change"
              value={maxOpenContractsChangeReason}
              onChange={(e) => setMaxOpenContractsChangeReason(e.target.value)}
              helperText={!isRegion
                ? 'This cluster is no longer a Region, so its maximum open contracts will be cleared. The change is recorded in its history.'
                : 'Recorded in the Region\'s maximum-open-contracts history.'}
            />
          </FormRow>
        )}
        {systemOfRecordChanged && (
          <FormRow>
            <TextField
              fullWidth
              required
              label="Reason for the system-of-record change"
              value={systemOfRecordChangeReason}
              onChange={(e) => setSystemOfRecordChangeReason(e.target.value)}
              helperText={!isRegion && initialSystemOfRecord
                ? 'This cluster is no longer a Region, so its system of record will be cleared. The change is recorded in its history.'
                : 'Recorded in the Region\'s system-of-record history.'}
            />
          </FormRow>
        )}

        <FormRow>
          <Box sx={{ width: '100%' }}>
            <EmployeeSearch
              value={selectedManager}
              onChange={handleManagerChange}
              label="Cluster Representative"
              placeholder="No representative assigned"
              width={400}
              height="56px"
              sx={{ width: '100%' }}
              showSelectedAvatar={true}
              disabled={true}
            />
            <Typography variant="body2" sx={{ mt: 1, color: '#94A3B8', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
              To assign or change the representative, use the Business Units tab and click the history icon
              <Box component="span" sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                backgroundColor: '#8B5CF6',
                color: 'white',
                fontSize: '9px',
                fontWeight: 'bold',
                lineHeight: 1
              }}>h</Box>
              on the cluster node.
            </Typography>
          </Box>
        </FormRow>
      </FormSection>
    </FormContainer>
  );

  const renderBusinessUnits = () => (
    <BusinessUnitsTab clusterId={id} onClusterManagerChange={setSelectedManager} />
  );

  /*
  const renderDepartments = () => (
    <DepartmentsTab clusterId={id} />
  );
  */

  const renderCompanies = () => (
    <CompaniesTab clusterId={id} />
  );

  const renderContent = () => {
    switch (activeTab) {
      case TABS.CLUSTER_DATA:
        return renderClusterData();
      case TABS.COMPANIES:
        return renderCompanies();
      case TABS.BUSINESS_UNITS:
        return renderBusinessUnits();
      //case TABS.DEPARTMENTS:
      //  return renderDepartments();
      default:
        return null;
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization/clusters')}>
            <FaArrowLeft /> Back to Clusters
          </BackButton>
          <Title>
            {mode === 'add' 
              ? 'Add New Cluster' 
              : `Edit Cluster | ${formData.name}`}
          </Title>
        </HeaderLeft>
        {activeTab === TABS.CLUSTER_DATA && (
          <HeaderButton
            onClick={() => handleSubmit()}
            disabled={peopleAboveNewLimit.length > 0 || regionWithChildren}
            style={peopleAboveNewLimit.length > 0 || regionWithChildren ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            title={peopleAboveNewLimit.length > 0 ? 'Some people hold more contracts than the new limit — see the field, or use Lower anyway' : undefined}
          >
            {mode === 'add' ? 'Create Cluster' : 'Update Cluster'}
          </HeaderButton>
        )}
      </Header>

      <TabContainer>
        <TabButton
          data-active={activeTab === TABS.CLUSTER_DATA}
          onClick={() => setActiveTab(TABS.CLUSTER_DATA)}
        >
          Cluster Data
        </TabButton>
        {mode === 'edit' && (
          <>
            <TabButton
              data-active={activeTab === TABS.COMPANIES}
              onClick={() => setActiveTab(TABS.COMPANIES)}
            >
              Companies
            </TabButton>
            <TabButton
              data-active={activeTab === TABS.BUSINESS_UNITS}
              onClick={() => setActiveTab(TABS.BUSINESS_UNITS)}
            >
              Business Units
            </TabButton>
            {/*
            <TabButton
              data-active={activeTab === TABS.DEPARTMENTS}
              onClick={() => setActiveTab(TABS.DEPARTMENTS)}
            >
              Departments
            </TabButton>
            */}
          </>
        )}
      </TabContainer>

      {renderContent()}

      {/* ADR-167 D6: "lower anyway" — recorded, existing contracts stay */}
      <Dialog open={confirmLowerAnyway} onClose={() => setConfirmLowerAnyway(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lower anyway?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${peopleAboveNewLimit.length === 1 ? '1 person keeps' : `${peopleAboveNewLimit.length} people keep`} more than ${limitImpact.newLimit} open contract${limitImpact.newLimit === 1 ? '' : 's'}. Their contracts are not closed, they cannot add more, and they are listed in the change history and as information in Employment Episode Discrepancies. New contracts in ${formData.name} are refused at the new limit from now on.`}
          </DialogContentText>
          {!maxOpenContractsChangeReason.trim() && (
            <Alert severity="info" sx={{ mt: 2 }}>Fill in the reason for the change before confirming.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLowerAnyway(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!maxOpenContractsChangeReason.trim()}
            onClick={() => { setConfirmLowerAnyway(false); handleSubmit({ keepExistingContracts: true }); }}
          >
            Lower anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClusterForm; 